import { useTranslation } from "react-i18next";

// ── Step-Indicator ────────────────────────────────────────────────────
// Geteilte Step-Leiste für Wizard und Editor (gleiches Look & Feel).
// Klickbar: besuchte Steps (≤ maxStep) frei anspringbar, der nächste
// unbesuchte Step (maxStep + 1) nur mit bestandener Weiter-Guard.
// Ohne maxStep (Editor) sind alle Steps frei anspringbar.
export default function StepIndicator({
  labels,
  current,
  maxStep,
  busy = false,
  canAdvanceTo,
  onSelect,
}: {
  labels: string[];
  current: number;
  maxStep?: number;
  busy?: boolean;
  canAdvanceTo?: (n: number) => boolean;
  onSelect: (n: number) => void;
}) {
  const { t } = useTranslation();
  const max = maxStep ?? labels.length;
  return (
    <div className="flex items-center gap-3 mb-12">
      {labels.map((label, idx) => {
        const n = idx + 1;
        const isCurrent = n === current;
        const visited = n <= max;
        const advanceable = n === max + 1 && (canAdvanceTo?.(n) ?? true) && !busy;
        const clickable = !isCurrent && (visited || advanceable);
        return (
          <div key={n} className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onSelect(n)}
              disabled={!clickable}
              aria-label={
                clickable
                  ? t("wizard.stepNav.goTo", { step: n, label })
                  : undefined
              }
              title={
                clickable
                  ? t("wizard.stepNav.goTo", { step: n, label })
                  : t("wizard.stepNav.locked")
              }
              className={`flex items-center gap-3 rounded-full transition ${
                clickable ? "cursor-pointer hover:opacity-80" : "cursor-not-allowed"
              }`}
            >
              <span
                className={`flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm ${
                  isCurrent
                    ? "bg-ca-accent text-ca-brand"
                    : visited
                    ? "bg-ca-accent text-ca-brand"
                    : advanceable
                    ? "bg-ca-card border border-ca-accent text-ca-surface-ink"
                    : "bg-ca-card border border-ca-surface-border text-ca-surface-muted"
                }`}
              >
                {n}
              </span>
              <span
                className={`text-sm font-semibold ${
                  isCurrent ? "text-ca-brand" : "text-ca-ink-muted"
                } ${clickable ? "underline decoration-dotted underline-offset-4" : ""}`}
              >
                {label}
              </span>
            </button>
            {n < labels.length && <span className="w-8 h-px bg-ca-border" />}
          </div>
        );
      })}
    </div>
  );
}
